/**
 * Phase 132 → 138A — Super Admin Invoices (/admin/livex/invoices)
 * List, filter by status, mark paid/void, real Mark Sent dispatch (email +
 * WhatsApp + in-app notif + audit log), Reissue, signed-token public viewer.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import '@/styles/livex.css';
import {
  ArrowLeft, Search, Loader2, AlertTriangle, ExternalLink, Check,
  Receipt, X, Calendar, Send, RotateCcw, Eye,
} from 'lucide-react';
import LivexEmptyState from '@/components/livex/LivexEmptyState';
import { LivexShimmerRow } from '@/components/livex/LivexShimmer';

function formatINR(p) { return `₹${(p / 100).toFixed(2)}`; }
function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
}

const STATUS_FILTERS = [
  { k: 'all', label: 'All' },
  { k: 'draft', label: 'Draft' },
  { k: 'sent', label: 'Sent' },
  { k: 'viewed', label: 'Viewed' },
  { k: 'paid', label: 'Paid' },
  { k: 'overdue', label: 'Overdue' },
  { k: 'void', label: 'Void' },
];

function StatusBadge({ status }) {
  const map = {
    paid:    { bg: 'bg-emerald-600', text: 'text-white' },
    sent:    { bg: 'bg-amber-500',   text: 'text-black' },
    viewed:  { bg: 'bg-blue-600',    text: 'text-white' },
    overdue: { bg: 'bg-rose-600',    text: 'text-white' },
    draft:   { bg: 'bg-slate-700',   text: 'text-white' },
    void:    { bg: 'bg-slate-600',   text: 'text-white' },
  };
  const cls = map[status] || map.draft;
  return (
    <span className={`text-[10px] font-black uppercase tracking-[0.1em] px-2 py-1 rounded ${cls.bg} ${cls.text}`}>{status}</span>
  );
}

export default function LivexAdminInvoices() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState(params.get('status') || 'all');
  const [busy, setBusy] = useState({});
  const [toast, setToast] = useState(null);

  const reload = async () => {
    try {
      const url = filter === 'all' ? '/livex/admin/invoices' : `/livex/admin/invoices?status=${filter}`;
      const { data } = await api.get(url);
      setList(data?.entries || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load');
    }
  };

  useEffect(() => { setLoading(true); reload().finally(() => setLoading(false)); }, [filter]);

  const patchStatus = async (id, status) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.patch(`/livex/admin/invoices/${id}`, { status });
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  // Phase 138A — real "Mark Sent" dispatch (email + WhatsApp + in-app)
  const dispatchInvoice = async (id, mode = 'mark_sent') => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const url = mode === 'reissue'
        ? `/livex/admin/invoices/${id}/reissue`
        : `/livex/admin/invoices/${id}/dispatch`;
      const { data } = await api.post(url);
      const d = data?.dispatch || {};
      const sentCount = (d.email?.sent ? 1 : 0) + (d.whatsapp?.sent ? 1 : 0) + (d.notification?.sent ? 1 : 0);
      const skipped = [];
      if (d.email?.skipped) skipped.push('email');
      if (d.whatsapp?.skipped) skipped.push('whatsapp');
      setToast({
        kind: 'ok',
        title: mode === 'reissue' ? 'Invoice reissued' : 'Invoice dispatched',
        body: `${sentCount} channel${sentCount === 1 ? '' : 's'} sent` + (skipped.length ? ` · skipped: ${skipped.join(', ')}` : ''),
      });
      setTimeout(() => setToast(null), 5000);
      await reload();
    } catch (e) {
      setToast({ kind: 'err', title: 'Dispatch failed', body: e?.response?.data?.detail || 'Try again' });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const filtered = list.filter((inv) => {
    if (!q) return true;
    return `${inv.invoice_number} ${inv.academy_name}`.toLowerCase().includes(q.toLowerCase());
  });

  const totalPaid = list.filter((i) => i.status === 'paid').reduce((a, i) => a + (i.total_inr_paise || 0), 0);
  const totalPending = list.filter((i) => ['draft', 'sent', 'viewed', 'overdue'].includes(i.status)).reduce((a, i) => a + (i.total_inr_paise || 0), 0);

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-admin-invoices">
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate('/admin/livex')} className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#FF5A1F] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Overview
          </button>
          <div className="text-xs tracking-[0.3em] font-black text-[#FF5A1F]">STREAMX · INVOICES</div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          data-testid="livex-admin-invoices-toast"
          className={`fixed top-20 right-4 z-50 max-w-sm p-4 rounded-xl border-2 backdrop-blur ${
            toast.kind === 'ok'
              ? 'bg-emerald-950/90 border-emerald-500 text-emerald-100'
              : 'bg-rose-950/90 border-rose-500 text-rose-100'
          }`}
          style={{ boxShadow: '0 16px 40px rgba(0,0,0,0.45)' }}
        >
          <div className="text-sm font-black mb-0.5 flex items-center gap-2">
            {toast.kind === 'ok' ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {toast.title}
          </div>
          <div className="text-xs font-medium opacity-90">{toast.body}</div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl sm:text-3xl font-black text-white">Invoices</h1>
          <Link to="/admin/livex/academies" data-testid="livex-admin-invoices-new"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white border-2 border-[#FF5A1F] rounded-lg"
            style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
            <Receipt className="h-4 w-4" /> Generate from Academy
          </Link>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <div className="livex-detail-key mb-1">Total Paid</div>
            <div className="text-2xl font-black text-white tabular-nums" data-testid="livex-admin-invoices-paid-total">{formatINR(totalPaid)}</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <div className="livex-detail-key mb-1">Total Pending</div>
            <div className="text-2xl font-black text-white tabular-nums" data-testid="livex-admin-invoices-pending-total">{formatINR(totalPending)}</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <div className="livex-detail-key mb-1">Count</div>
            <div className="text-2xl font-black text-white tabular-nums">{list.length}</div>
          </div>
        </div>

        {/* Search + filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8] pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by invoice number or academy..."
              data-testid="livex-admin-invoices-search"
              className="livex-input pl-10"
            />
          </div>
          {STATUS_FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`livex-admin-invoices-filter-${f.k}`}
              className={`px-3 py-2.5 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${
                filter === f.k ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="p-3 bg-rose-950 border-2 border-rose-700 rounded-lg text-rose-100 text-sm font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-2" data-testid="livex-admin-invoices-loading">
            {Array.from({ length: 5 }).map((_, i) => <LivexShimmerRow key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <LivexEmptyState
            testid="livex-admin-invoices-empty"
            icon={Receipt}
            title={q || filter !== 'all' ? 'No invoices match this filter' : 'No invoices yet'}
            body={q || filter !== 'all'
              ? 'Clear the search or pick a different status to see all invoices.'
              : 'Generate your first invoice from the Academies page. Invoice numbers auto-increment as INV-YYYY-NNNN.'}
            ctaLabel="Go to Academies"
            ctaTo="/admin/livex/academies"
            ctaIcon={Receipt}
            secondaryLabel="Back to overview"
            secondaryTo="/admin/livex"
            compact
          />
        ) : (
          <div className="space-y-2">
            {filtered.map((inv) => {
              const period = inv.billing_period_label || inv.billing_cycle;
              return (
                <div key={inv.id} className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl livex-card-lift flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`livex-admin-invoice-${inv.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Receipt className="h-5 w-5 text-[#FF5A1F] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-black text-white font-mono">{inv.invoice_number}</span>
                        <StatusBadge status={inv.status} />
                        <span className="text-[10px] font-bold text-[#94A3B8] uppercase">{period}</span>
                        {(inv.dispatch_count || 0) > 0 && (
                          <span className="text-[10px] font-bold text-[#94A3B8] inline-flex items-center gap-1" title={`Dispatched ${inv.dispatch_count} times`}>
                            <Send className="h-3 w-3" /> {inv.dispatch_count}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] font-bold text-[#94A3B8] mt-0.5 truncate">
                        {inv.academy_name} · <Calendar className="inline h-3 w-3 mr-1" />{formatDate(inv.issued_at)} · due {formatDate(inv.due_at)}
                      </div>
                    </div>
                    <span className="text-base font-black text-white tabular-nums whitespace-nowrap sm:hidden">{formatINR(inv.total_inr_paise)}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:flex-shrink-0">
                    <span className="hidden sm:inline text-base font-black text-white tabular-nums">{formatINR(inv.total_inr_paise)}</span>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/livex/admin/invoices/${inv.id}/html`} target="_blank" rel="noopener noreferrer"
                      data-testid={`livex-admin-invoice-${inv.id}-view`}
                      aria-label={`Open invoice ${inv.invoice_number}`}
                      className="inline-flex items-center justify-center w-9 h-9 text-[#94A3B8] hover:text-[#FF5A1F] hover:bg-[#0c1a2e] rounded-lg transition-colors" title="Open invoice">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    {inv.status !== 'paid' && (
                      <button onClick={() => patchStatus(inv.id, 'paid')} disabled={busy[inv.id]}
                        data-testid={`livex-admin-invoice-${inv.id}-mark-paid`}
                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-600 border-2 border-emerald-600 rounded-lg transition-all disabled:opacity-50">
                        {busy[inv.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Mark Paid
                      </button>
                    )}
                    {/* Phase 138A — real dispatch on draft, reissue on sent/viewed/overdue */}
                    {inv.status === 'draft' && (
                      <button onClick={() => dispatchInvoice(inv.id, 'mark_sent')} disabled={busy[inv.id]}
                        data-testid={`livex-admin-invoice-${inv.id}-mark-sent`}
                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold text-white bg-amber-700 hover:bg-amber-600 border-2 border-amber-600 rounded-lg transition-all disabled:opacity-50"
                        title="Send via email + WhatsApp + in-app notification">
                        {busy[inv.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Mark Sent
                      </button>
                    )}
                    {['sent', 'viewed', 'overdue'].includes(inv.status) && (
                      <button onClick={() => dispatchInvoice(inv.id, 'reissue')} disabled={busy[inv.id]}
                        data-testid={`livex-admin-invoice-${inv.id}-reissue`}
                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold text-white bg-blue-700 hover:bg-blue-600 border-2 border-blue-600 rounded-lg transition-all disabled:opacity-50"
                        title="Re-send invoice via email + WhatsApp">
                        {busy[inv.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Reissue
                      </button>
                    )}
                    {inv.status !== 'void' && inv.status !== 'paid' && (
                      <button onClick={() => patchStatus(inv.id, 'void')} disabled={busy[inv.id]}
                        data-testid={`livex-admin-invoice-${inv.id}-void`}
                        aria-label={`Void invoice ${inv.invoice_number}`}
                        className="inline-flex items-center justify-center w-9 h-9 text-[#94A3B8] hover:text-rose-300 hover:bg-rose-950/30 rounded-lg transition-colors" title="Void">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
